import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Loader2, Save, Send, ShieldCheck, Mail, MessageSquare, Smartphone, Stethoscope, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { devLog } from "@/lib/devLog";

// Super-admin editable credentials for M-Pesa, WhatsApp, SMS, Email.
const INTEGRATION_SECTIONS = [
  {
    key: "mpesa",
    label: "M-Pesa Daraja",
    sub: "Safaricom Daraja sandbox or production credentials.",
    docs: "https://developer.safaricom.co.ke",
    fields: [
      { name: "MPESA_ENV", label: "Environment", placeholder: "sandbox or production", type: "select", options: ["sandbox", "production", "placeholder"] },
      { name: "MPESA_TRANSACTION_TYPE", label: "Transaction type", placeholder: "Paybill vs Till", type: "select", options: ["CustomerPayBillOnline", "CustomerBuyGoodsOnline"] },
      { name: "MPESA_CONSUMER_KEY", label: "Consumer Key", placeholder: "From your Daraja app", secret: true },
      { name: "MPESA_CONSUMER_SECRET", label: "Consumer Secret", placeholder: "From your Daraja app", secret: true },
      { name: "MPESA_SHORTCODE", label: "Shortcode (Paybill OR Till number)", placeholder: "e.g. 174379 (sandbox) or your Paybill/Till" },
      { name: "MPESA_HEADOFFICE_SHORTCODE", label: "Head Office Shortcode (Till only)", placeholder: "Leave blank for Paybill. For Till, paste your HQ store number." },
      { name: "MPESA_PASSKEY", label: "Passkey", placeholder: "STK Push passkey", secret: true },
      { name: "MPESA_CALLBACK_URL", label: "Callback URL", placeholder: "https://print.ctpkenya.com/api/payments/mpesa/callback" },
    ],
  },
  {
    key: "whatsapp",
    label: "Meta WhatsApp Cloud API",
    sub: "Direct Meta Business Cloud API (no Twilio). Free up to 1,000 utility conversations/month.",
    docs: "https://developers.facebook.com",
    fields: [
      { name: "WHATSAPP_PHONE_NUMBER_ID", label: "Phone Number ID", placeholder: "From WhatsApp → API Setup" },
      { name: "WHATSAPP_BUSINESS_ACCOUNT_ID", label: "Business Account ID", placeholder: "WABA ID" },
      { name: "WHATSAPP_ACCESS_TOKEN", label: "Access Token", placeholder: "System User permanent token (NOT 24h temp)", secret: true },
      { name: "WHATSAPP_API_VERSION", label: "Graph API Version", placeholder: "v21.0" },
      { name: "WHATSAPP_DEFAULT_LANG", label: "Default Language", placeholder: "en" },
      { name: "WHATSAPP_TEMPLATE_READY", label: "Template name: order_ready", placeholder: "order_ready" },
      { name: "WHATSAPP_TEMPLATE_DISPATCHED", label: "Template name: order_dispatched", placeholder: "order_dispatched" },
      { name: "WHATSAPP_TEMPLATE_CONFIRMATION", label: "Template name: order_confirmation", placeholder: "order_confirmation" },
      { name: "META_VERIFY_TOKEN", label: "Webhook verify token", placeholder: "Any random string — paste the same into Meta", secret: true },
      { name: "META_APP_SECRET", label: "App Secret (signs webhooks)", placeholder: "From Meta App → Settings → Basic", secret: true },
    ],
  },
  {
    key: "sms",
    label: "Africa's Talking SMS",
    sub: "Used for SMS notifications (works alongside WhatsApp).",
    docs: "https://account.africastalking.com",
    fields: [
      { name: "AT_USERNAME", label: "Username", placeholder: "sandbox or your live username" },
      { name: "AT_API_KEY", label: "API Key", placeholder: "From AT dashboard", secret: true },
      { name: "AT_SENDER_ID", label: "Sender ID (optional)", placeholder: "e.g. CTPKENYA (must be pre-registered)" },
    ],
  },
  {
    key: "email",
    label: "Resend (Email)",
    sub: "Transactional email receipts and order updates. Sandbox sender works out of the box; verify a domain in Resend to send from your own address.",
    docs: "https://resend.com/api-keys",
    fields: [
      { name: "RESEND_API_KEY", label: "API Key", placeholder: "re_••••••••", secret: true },
      { name: "RESEND_FROM", label: "From address", placeholder: "onboarding@resend.dev · or no-reply@yourdomain.com" },
    ],
  },
];

export default function IntegrationsTab() {
  const [sections, setSections] = useState(null);
  const [status, setStatus] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({});
  const [testResults, setTestResults] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get("/admin/integrations");
      setSections(data.sections);
      setStatus(data.status);
      setDraft({});
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not load integrations");
    }
  };
  useEffect(() => { load(); }, []);

  const dirty = Object.keys(draft).length > 0;
  const [otpOpen, setOtpOpen] = useState(false);
  const [otpToken, setOtpToken] = useState(null);
  const [otpSentTo, setOtpSentTo] = useState(null);
  const [otpChannel, setOtpChannel] = useState("email");
  const [otpPhone, setOtpPhone] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [requestingOtp, setRequestingOtp] = useState(false);

  const openOtpFlow = async () => {
    if (!dirty) {
      toast.info("Nothing to save");
      return;
    }
    // Just open the channel-picker modal; the actual OTP is requested
    // when the user picks a channel.
    setOtpToken(null);
    setOtpSentTo(null);
    setOtpChannel("email");
    setOtpPhone("");
    setOtpCode("");
    setOtpOpen(true);
  };

  const requestOtp = async (channel, phone) => {
    setRequestingOtp(true);
    try {
      const payload = { channel };
      if (phone) payload.phone = phone;
      const { data } = await api.post("/admin/integrations/save-otp/request", payload);
      setOtpToken(data.token);
      setOtpSentTo(data.sent_to);
      setOtpChannel(data.channel);
      setOtpCode("");
      const label = data.channel === "email" ? "email" : data.channel === "sms" ? "SMS" : "WhatsApp";
      if (data.placeholder_mode) {
        toast.info(data.message || `${label} provider in placeholder mode`);
      } else if (data.reused) {
        toast.success(`Same code resent via ${label} to ${data.sent_to}`);
      } else {
        toast.success(`Code sent via ${label} to ${data.sent_to}`);
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not send confirmation code");
    } finally {
      setRequestingOtp(false);
    }
  };

  const save = async () => {
    if (!otpToken || !otpCode.trim()) {
      toast.error("Enter the 6-digit code from your email");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/admin/integrations", {
        payload: draft,
        otp_token: otpToken,
        otp_code: otpCode.trim(),
      });
      setSections(data.sections);
      toast.success(`Saved ${data.written} field${data.written === 1 ? "" : "s"} · live`);
      setDraft({});
      setOtpOpen(false);
      setOtpToken(null);
      setOtpCode("");
      try { const r = await api.get("/admin/integrations"); setStatus(r.data.status); } catch (err) { devLog.debug("post-save status refresh failed", err); }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const runTest = async (sectionKey) => {
    setTesting((t) => ({ ...t, [sectionKey]: true }));
    setTestResults((r) => ({ ...r, [sectionKey]: null }));
    try {
      const url = `/admin/integrations/test/${sectionKey}`;
      const { data } = await api.post(url, {});
      setTestResults((r) => ({ ...r, [sectionKey]: data }));
      if (data.ok) toast.success(`${sectionKey.toUpperCase()} · ${data.message || "OK"}`);
      else toast.error(`${sectionKey.toUpperCase()} test failed: ${data.reason || "unknown"}`);
    } catch (e) {
      const reason = formatApiError(e?.response?.data?.detail) || e.message;
      setTestResults((r) => ({ ...r, [sectionKey]: { ok: false, reason } }));
      toast.error(reason);
    } finally {
      setTesting((t) => ({ ...t, [sectionKey]: false }));
    }
  };

  if (!sections) return <Loader2 className="w-6 h-6 animate-spin text-[#059669]" />;

  return (
    <div className="space-y-6" data-testid="integrations-tab">
      <div className="bg-white border border-[#E5E5E5] p-5">
        <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252] mb-1">§ External services</div>
        <h2 className="font-display font-black text-2xl">Integrations</h2>
        <p className="text-xs text-[#525252] mt-1 max-w-2xl">
          Update credentials here and they take effect immediately — no restart needed. Secrets are masked once saved; leave a masked field untouched to keep the existing value.
        </p>
      </div>

      {INTEGRATION_SECTIONS.map((section) => (
        <IntegrationSection
          key={section.key}
          section={section}
          sectionData={sections[section.key] || {}}
          status={status?.[section.key]}
          draft={draft}
          setDraft={setDraft}
          onTest={() => runTest(section.key)}
          testing={!!testing[section.key]}
          result={testResults[section.key]}
        />
      ))}

      <div className="sticky bottom-4 bg-white border-2 border-[#0A0A0A] p-4 flex flex-wrap items-center justify-between gap-3 shadow-lg">
        <div className="text-xs text-[#525252]">
          {dirty ? (
            <span className="text-[#059669] font-bold uppercase tracking-[0.15em]">
              {Object.keys(draft).length} unsaved change{Object.keys(draft).length === 1 ? "" : "s"}
            </span>
          ) : (
            <span className="uppercase tracking-[0.15em]">No pending changes</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setDraft({})}
            disabled={!dirty}
            data-testid="integrations-discard"
            className="px-4 py-2 text-[10px] uppercase tracking-[0.2em] font-bold border border-[#E5E5E5] text-[#525252] hover:border-[#0A0A0A] disabled:opacity-50"
          >
            Discard
          </button>
          <button
            onClick={openOtpFlow}
            disabled={!dirty || saving || requestingOtp}
            data-testid="integrations-save"
            className="inline-flex items-center gap-2 px-5 py-2 text-[10px] uppercase tracking-[0.2em] font-bold bg-[#059669] text-white hover:bg-[#047857] disabled:opacity-50"
          >
            {requestingOtp ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />} Save &amp; sync
          </button>
        </div>
      </div>
      {otpOpen && (
        <OtpConfirmModal
          channel={otpChannel}
          setChannel={setOtpChannel}
          phone={otpPhone}
          setPhone={setOtpPhone}
          sentTo={otpSentTo}
          hasToken={!!otpToken}
          requesting={requestingOtp}
          onRequest={(ch) => requestOtp(ch, otpPhone)}
          otpCode={otpCode}
          setOtpCode={setOtpCode}
          onCancel={() => { setOtpOpen(false); setOtpCode(""); setOtpToken(null); }}
          onConfirm={save}
          saving={saving}
        />
      )}
    </div>
  );
}

function OtpConfirmModal({
  channel, setChannel, phone, setPhone, sentTo, hasToken, requesting,
  onRequest, otpCode, setOtpCode, onCancel, onConfirm, saving,
}) {
  const channels = [
    { key: "email",    label: "Email",    Icon: Mail },
    { key: "sms",      label: "SMS",      Icon: Smartphone },
    { key: "whatsapp", label: "WhatsApp", Icon: MessageSquare },
  ];
  const needsPhone = channel !== "email";

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="integrations-otp-modal">
      <div className="bg-white border-2 border-[#0A0A0A] w-full max-w-sm p-6 space-y-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252]">§ Confirmation required</div>
          <h3 className="font-display font-black text-2xl mt-1">Verify it&apos;s you</h3>
          <p className="text-xs text-[#525252] mt-2">
            Pick where you want the 6-digit code, then enter it below to apply your integration changes.
          </p>
        </div>

        {/* Channel picker */}
        <div className="grid grid-cols-3 gap-2">
          {channels.map(({ key, label, Icon }) => {
            const active = channel === key;
            return (
              <button
                key={key}
                onClick={() => { setChannel(key); }}
                data-testid={`integrations-otp-channel-${key}`}
                className={`flex flex-col items-center gap-1 px-2 py-3 border-2 text-[10px] uppercase tracking-[0.15em] font-bold ${active ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5E5] text-[#525252] hover:border-[#0A0A0A]"}`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            );
          })}
        </div>

        {needsPhone && (
          <div>
            <label className="block text-[9px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">
              Phone (leave blank to use admin profile phone)
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="07XXXXXXXX"
              data-testid="integrations-otp-phone"
              className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none text-sm mono"
            />
          </div>
        )}

        <button
          onClick={() => onRequest(channel)}
          disabled={requesting}
          data-testid="integrations-otp-send"
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 border-2 border-[#0A0A0A] text-[#0A0A0A] text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-[#0A0A0A] hover:text-white disabled:opacity-50"
        >
          {requesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          {hasToken ? "Resend code" : "Send code"}
        </button>

        {hasToken && (
          <>
            <div className="text-xs text-[#525252]">
              Sent to <span className="mono font-bold text-[#0A0A0A]">{sentTo}</span> · check your {channel === "email" ? "inbox" : channel === "sms" ? "SMS" : "WhatsApp"}.
            </div>
            <div>
              <label className="block text-[9px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">Confirmation code</label>
              <input
                type="text"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                autoFocus
                inputMode="numeric"
                placeholder="••••••"
                data-testid="integrations-otp-input"
                className="w-full px-3 py-2 border-2 border-[#0A0A0A] outline-none text-center text-2xl font-display font-black tracking-[0.4em] mono"
              />
              <p className="text-[10px] text-[#525252] mt-1">Expires in 10 minutes · 5 wrong attempts and you&apos;ll need a new code. Resending within 10 min re-sends the same code.</p>
            </div>
          </>
        )}

        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={saving}
            className="px-4 py-2 border border-[#E5E5E5] text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] hover:border-[#0A0A0A] hover:text-[#0A0A0A] disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={saving || !hasToken || otpCode.length < 4}
            data-testid="integrations-otp-confirm"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#059669] text-white text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-[#047857] disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Confirm &amp; save
          </button>
        </div>
      </div>
    </div>
  );
}

function IntegrationSection({ section, sectionData, status, draft, setDraft, onTest, testing, result }) {
  const enabled = status?.enabled ?? status?.credentials_present ?? false;
  const [tmplStatus, setTmplStatus] = useState(null);
  const [tmplLoading, setTmplLoading] = useState(false);
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [sendTestResult, setSendTestResult] = useState(null);
  const checkTemplates = async () => {
    setTmplLoading(true);
    try {
      const { data } = await api.get("/admin/integrations/whatsapp/templates-status");
      setTmplStatus(data);
    } catch (e) {
      setTmplStatus({ ok: false, reason: e?.response?.data?.detail || "failed" });
    } finally {
      setTmplLoading(false);
    }
  };
  const runDiagnose = async () => {
    setDiagLoading(true);
    setDiag(null);
    try {
      const { data } = await api.get(`/admin/integrations/${section.key}/diagnose`);
      setDiag(data);
      if (data.ok) toast.success(data.summary || `${section.label} OK`);
      else toast.error(data.summary || `${section.label} misconfigured`);
    } catch (e) {
      const reason = formatApiError(e?.response?.data?.detail) || e.message;
      setDiag({ ok: false, summary: reason, checks: [] });
      toast.error(reason);
    } finally {
      setDiagLoading(false);
    }
  };
  const sendTestMessage = async () => {
    const phone = testPhone.trim();
    if (!phone) {
      toast.error("Enter a phone number first (e.g. 0712345678)");
      return;
    }
    setSendingTest(true);
    setSendTestResult(null);
    try {
      const { data } = await api.post("/admin/integrations/whatsapp/send-test", { phone });
      setSendTestResult(data);
      if (data.ok) toast.success(data.message || "Test message sent");
      else toast.error(data.reason || "Send failed");
    } catch (e) {
      const reason = formatApiError(e?.response?.data?.detail) || e.message;
      setSendTestResult({ ok: false, reason });
      toast.error(reason);
    } finally {
      setSendingTest(false);
    }
  };
  return (
    <section className="bg-white border border-[#E5E5E5]" data-testid={`integration-section-${section.key}`}>
      <div className="p-5 border-b border-[#E5E5E5] flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-display font-black text-lg">{section.label}</h3>
            <span
              className={`px-2 py-0.5 text-[9px] uppercase tracking-[0.2em] font-bold border ${enabled ? "border-[#059669] bg-[#059669]/10 text-[#059669]" : "border-[#F59E0B] bg-[#F59E0B]/10 text-[#92400E]"}`}
              data-testid={`integration-status-${section.key}`}
            >
              {enabled ? "Live" : "Placeholder"}
            </span>
          </div>
          <p className="text-xs text-[#525252] max-w-xl">{section.sub}</p>
          <a
            href={section.docs}
            target="_blank"
            rel="noreferrer"
            className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#0A0A0A] hover:text-[#059669] mt-2 inline-block"
          >
            Get credentials →
          </a>
        </div>
        <div className="flex flex-col items-end gap-2">
          <button
            onClick={onTest}
            disabled={testing}
            data-testid={`integration-test-${section.key}`}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-[10px] uppercase tracking-[0.2em] font-bold border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white disabled:opacity-50"
          >
            {testing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Test connection
          </button>
          {result && (
            <div className={`text-[10px] mono px-2 py-1 max-w-xs text-right ${result.ok ? "text-[#059669]" : "text-[#DC2626]"}`}>
              {result.ok ? `✓ ${result.message || "OK"}` : `✗ ${result.reason || "Failed"}`}
            </div>
          )}
        </div>
      </div>
      <div className="p-5 grid md:grid-cols-2 gap-4">
        {section.fields.map((f) => {
          const current = sectionData[f.name] || {};
          const draftVal = draft[f.name];
          const value = draftVal !== undefined ? draftVal : current.value || "";
          const isSecret = !!(f.secret || current.secret);
          return (
            <div key={f.name}>
              <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">
                {f.label}
                {isSecret && current.set && <span className="ml-2 text-[#059669]">· stored</span>}
              </label>
              {f.type === "select" ? (
                <select
                  value={value}
                  onChange={(e) => setDraft((d) => ({ ...d, [f.name]: e.target.value }))}
                  data-testid={`integration-field-${f.name}`}
                  className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none text-sm"
                >
                  {(f.options || []).map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={value}
                  placeholder={f.placeholder}
                  onChange={(e) => setDraft((d) => ({ ...d, [f.name]: e.target.value }))}
                  data-testid={`integration-field-${f.name}`}
                  className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none text-sm mono"
                />
              )}
            </div>
          );
        })}
      </div>
      {section.key === "mpesa" && (
        <div className="px-5 pb-5">
          <div className="flex items-center justify-between flex-wrap gap-2 border-t border-[#E5E5E5] pt-4">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252]">
              Daraja health checks
            </div>
            <button
              onClick={runDiagnose}
              disabled={diagLoading}
              data-testid="mpesa-diagnose"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold border-2 border-[#0A0A0A] bg-[#0A0A0A] text-white hover:bg-white hover:text-[#0A0A0A] disabled:opacity-50"
            >
              {diagLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Stethoscope className="w-3 h-3" />}
              Diagnose
            </button>
          </div>
          {diag && <DiagnoseResult diag={diag} testId="mpesa-diagnose-result" />}
        </div>
      )}
      {section.key === "whatsapp" && (
        <div className="px-5 pb-5">
          <div className="flex items-center justify-between flex-wrap gap-2 border-t border-[#E5E5E5] pt-4">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252]">
              Meta health checks
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={runDiagnose}
                disabled={diagLoading}
                data-testid="whatsapp-diagnose"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold border-2 border-[#0A0A0A] bg-[#0A0A0A] text-white hover:bg-white hover:text-[#0A0A0A] disabled:opacity-50"
              >
                {diagLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Stethoscope className="w-3 h-3" />}
                Diagnose
              </button>
              <button
                onClick={checkTemplates}
                disabled={tmplLoading}
                data-testid="check-templates"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white disabled:opacity-50"
              >
                {tmplLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />}
                Check templates
              </button>
            </div>
          </div>

          {diag && <DiagnoseResult diag={diag} testId="whatsapp-diagnose-result" />}

          {/* Send-test: fire the real approved order_ready template to any phone */}
          <div className="mt-4 border-t border-[#E5E5E5] pt-4">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252] mb-2">
              Send real test message
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="tel"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="0712345678 or +2547XXXXXXXX"
                data-testid="whatsapp-test-phone"
                className="flex-1 min-w-[220px] px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none text-sm mono"
              />
              <button
                onClick={sendTestMessage}
                disabled={sendingTest || !testPhone.trim()}
                data-testid="whatsapp-send-test"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-[10px] uppercase tracking-[0.2em] font-bold border-2 border-[#059669] bg-[#059669] text-white hover:bg-[#047857] hover:border-[#047857] disabled:opacity-50"
              >
                {sendingTest ? <Loader2 className="w-3 h-3 animate-spin" /> : <MessageSquare className="w-3 h-3" />}
                Send test
              </button>
            </div>
            <p className="text-[10px] text-[#525252] mt-1.5">
              Fires the approved <span className="mono font-bold">order_ready</span> template with dummy details. Costs 1 Meta utility conversation credit.
            </p>
            {sendTestResult && (
              <div className={`mt-2 px-3 py-2 border text-[11px] mono ${sendTestResult.ok ? "border-[#059669] bg-[#059669]/10 text-[#059669]" : "border-[#DC2626] bg-[#DC2626]/10 text-[#DC2626]"}`}>
                {sendTestResult.ok ? `✓ ${sendTestResult.message}` : `✗ ${sendTestResult.reason}`}
              </div>
            )}
          </div>

          {tmplStatus && (
            <div className="mt-3 space-y-1.5" data-testid="templates-status">
              {!tmplStatus.ok && (
                <div className="text-[11px] text-[#DC2626] mono">{tmplStatus.reason || "Could not query Meta"}</div>
              )}
              {tmplStatus.ok && Object.entries(tmplStatus.results || {}).map(([k, t]) => (
                <div key={k} className="flex items-center justify-between border border-[#E5E5E5] px-3 py-1.5 text-xs">
                  <div className="mono font-bold">{t.name}</div>
                  <div className="flex items-center gap-2">
                    {t.category && <span className="text-[10px] uppercase tracking-[0.15em] text-[#525252]">{t.category}</span>}
                    <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] font-bold border ${
                      t.approved
                        ? "border-[#059669] bg-[#059669]/10 text-[#059669]"
                        : t.status === "NOT_FOUND"
                          ? "border-[#DC2626] bg-[#DC2626]/10 text-[#DC2626]"
                          : "border-[#F59E0B] bg-[#F59E0B]/10 text-[#92400E]"
                    }`}>
                      {t.approved ? "✓ Approved" : t.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}


function DiagnoseResult({ diag, testId }) {
  return (
    <div className="mt-3 space-y-2" data-testid={testId}>
      <div className={`px-3 py-2 border-2 text-xs font-bold uppercase tracking-[0.15em] ${diag.ok ? "border-[#059669] bg-[#059669]/10 text-[#059669]" : "border-[#DC2626] bg-[#DC2626]/10 text-[#DC2626]"}`}>
        {diag.ok ? "✓ " : "✗ "}{diag.summary}
      </div>
      {(diag.checks || []).map((c) => {
        const Icon = c.ok ? CheckCircle2 : c.severity === "warning" ? AlertTriangle : XCircle;
        const color = c.ok ? "text-[#059669]" : c.severity === "warning" ? "text-[#92400E]" : "text-[#DC2626]";
        const bg = c.ok ? "bg-[#059669]/5 border-[#059669]/30" : c.severity === "warning" ? "bg-[#F59E0B]/5 border-[#F59E0B]/30" : "bg-[#DC2626]/5 border-[#DC2626]/30";
        return (
          <div key={c.key} className={`border ${bg} px-3 py-2 flex gap-2 items-start`}>
            <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${color}`} />
            <div className="min-w-0 flex-1">
              <div className="text-xs font-bold text-[#0A0A0A]">{c.label}</div>
              <div className="text-[11px] text-[#525252] mono break-words leading-relaxed mt-0.5">{c.detail}</div>
            </div>
          </div>
        );
      })}
      {diag.identity && (
        <div className="border border-[#E5E5E5] px-3 py-2 text-[10px] text-[#525252] mono">
          <div className="uppercase tracking-[0.2em] font-bold text-[#0A0A0A] mb-1">Identity</div>
          {Object.entries(diag.identity).map(([k, v]) => {
            if (v === null || v === undefined || v === "") return null;
            const display = Array.isArray(v) ? v.join(", ") : String(v);
            return <div key={k} className="break-words">{k}: {display}</div>;
          })}
        </div>
      )}
    </div>
  );
}
